import { SEOHead } from "components/SEO"
import { useGlobalSEO, useSocialLinks } from "effects/useSiteContent"
import { generateHomePageGraphSchema } from "utils/structuredData"
import BuilderFirstSection from "components/pages/home/BuilderFirstSection"
import DataArchitectureSection from "components/pages/home/DataArchitectureSection"
import HeroSection from "components/pages/home/HeroSection"
import LogoTicker from "components/pages/home/LogoTicker"
import UseCasesSection from "components/pages/home/UseCasesSection"
import WhyPresetSection from "components/pages/home/WhyPresetSection"
import DividerSection from "components/ui/Blocks/DividerSection"
import DoubleCTASection from "components/ui/Blocks/DoubleCTASection"
import Layout from "components/ui/Layout"

function Home({ location: { pathname } }) {
  return (
    <Layout
      headerDark
      headerFloating
      contentSpaced
      trackingName="Home"
      pageName="home"
      pagePath={pathname}
    >
      <div>
        <HeroSection />
        <LogoTicker pauseOnHover={false} />
      </div>
      <WhyPresetSection />
      <UseCasesSection />
      <DataArchitectureSection />
      <BuilderFirstSection />
      <DividerSection />
      <DoubleCTASection trackingName="Home" />
    </Layout>
  )
}

// FAQs for homepage (used for schema markup)
const homepageFaqs = [
  {
    question: "What is Preset?",
    answer:
      "Preset is a fully managed cloud analytics platform built on Apache Superset. It supports data exploration, dashboard creation, embedded analytics, and enterprise deployment options.",
  },
  {
    question: "Is Preset free to use?",
    answer:
      "Preset offers a free Starter plan for up to 5 users, with the option to upgrade as your team grows.",
  },
  {
    question: "What is the relationship between Preset and Apache Superset?",
    answer:
      "Apache Superset is the open-source project. Preset is a fully managed cloud platform built on Superset that adds managed infrastructure, enterprise features, and support.",
  },
  {
    question: "What databases does Preset connect to?",
    answer:
      "Preset supports 40+ database connectors, including Snowflake, BigQuery, Databricks, Redshift, PostgreSQL, and MySQL.",
  },
  {
    question: "Can I embed Preset dashboards in my application?",
    answer:
      "Yes. Preset supports embedded analytics for applications and customer-facing experiences.",
  },
  {
    question: "Is Preset secure and compliant?",
    answer:
      "Preset offers enterprise deployment options with security features including SOC 2 Type II and HIPAA-related options, depending on plan and environment.",
  },
  {
    question: "Does Preset offer AI-powered analytics?",
    answer:
      "Yes. Preset includes AI features such as Preset Chatbot for natural language queries and Preset MCP (Model Context Protocol) for integrating Preset with AI agents and tools. Available for Enterprise customers.",
  },
]

// Export a Head component for Gatsby Head API
export function Head() {
  const globalSEO = useGlobalSEO()
  const socialLinks = useSocialLinks()

  const logo = globalSEO.socialPreviewImage?.startsWith("http")
    ? globalSEO.socialPreviewImage
    : `https://preset.io${globalSEO.socialPreviewImage}`

  const graphSchema = generateHomePageGraphSchema({
    name: globalSEO.siteTitle || "Preset",
    url: "https://preset.io",
    logo,
    description:
      globalSEO.defaultDescription ||
      "Preset is a fully managed cloud platform built on Apache Superset for data exploration, dashboards, and analytics.",
    pageTitle: "AI-Native BI Built on Apache Superset™ | Preset",
    socialLinks,
    faqs: homepageFaqs,
  })

  return (
    <SEOHead
      title="AI-Native BI Built on Apache Superset™"
      description="AI-native business intelligence built on Apache Superset™. Dashboards, embedded analytics, self-service exploration, and conversational AI — all open source, enterprise-grade, and built for the AI era."
      pageUrl="https://preset.io/"
      pageType="website"
      structuredData={[graphSchema]}
    />
  )
}

export default Home
